import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  FRAMEWORK_DISCLOSURE_COPY,
  NAV_LABELS,
  OWASP_MAPPING_LABELS,
  STATUS_DESCRIPTIONS,
  STATUS_LABELS,
  normalizeSecurityStatus,
} from '@/lib/vocabulary/securityTerminology';
import { formatTimestampSafe, getTimestampMeta } from '@/lib/utils/formatTimestamp';
import {
  CONTROL_ALIASES,
  getControlRegistryEntry,
  isKnownControlKey,
  resolveControlKey,
} from '@/lib/controlRegistry';

const formatControlName = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const badgeMeta = (badge: 'covered' | 'partial' | 'action' | 'planned', labelOverride?: string) => {
  switch (badge) {
    case 'covered':
      return { label: STATUS_LABELS.covered, variant: 'success' as const };
    case 'action':
      return { label: STATUS_LABELS.actionRequired, variant: 'danger' as const };
    case 'planned':
      return { label: STATUS_LABELS.monitoringPlanned, variant: 'neutral' as const };
    default:
      return { label: labelOverride ?? STATUS_LABELS.needsHost, variant: 'warning' as const };
  }
};

const bucketPriority: Record<'covered' | 'managed' | 'needs_host' | 'action' | 'planned', number> = {
  action: 4,
  needs_host: 3,
  managed: 2,
  covered: 1,
  planned: 0,
};

const bucketToBadge = (bucket: 'covered' | 'managed' | 'needs_host' | 'action' | 'planned') => {
  if (bucket === 'action') return 'action';
  if (bucket === 'covered') return 'covered';
  if (bucket === 'planned') return 'planned';
  return 'partial';
};

const bucketToLabel = (bucket: 'covered' | 'managed' | 'needs_host' | 'action' | 'planned') => {
  switch (bucket) {
    case 'managed':
      return STATUS_LABELS.managed;
    case 'needs_host':
      return STATUS_LABELS.needsHost;
    case 'action':
      return STATUS_LABELS.actionRequired;
    case 'covered':
      return STATUS_LABELS.covered;
    default:
      return STATUS_LABELS.monitoringPlanned;
  }
};

type StatusFilter = 'all' | 'action' | 'managed' | 'needs_host' | 'covered';

const formatTimestamp = (value?: string | null) => {
  const meta = getTimestampMeta(value);
  return { label: meta.label, title: meta.title || 'Not reported by site yet' };
};

const formatAge = (valueMs?: number | null) => {
  if (!valueMs || valueMs <= 0) return '—';
  const minutes = Math.floor(valueMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

const CONTROL_COPY: Record<string, { meaning: string; shadowScan: string; action: string }> = {
  mfa_coverage_snapshot: {
    meaning: 'Tracks how many admins have enrolled MFA.',
    shadowScan: 'Monitors MFA coverage reported by the connector.',
    action: 'Enroll remaining admins in MFA.',
  },
  admin_geo_guard: {
    meaning: 'Tracks where admin access originates and whether allow/deny rules are active.',
    shadowScan: 'Monitors admin access locations and policy changes from the connector.',
    action: 'Manage allowed locations in Admin Access Location Rules.',
  },
  autoupdate_plugins_detect: {
    meaning: 'Checks whether plugin auto-updates are enabled.',
    shadowScan: 'Monitors update posture and policy signals.',
    action: 'Enable plugin auto-updates (allowlist recommended).',
  },
  // Source: plugin canonical copy (password policy control).
  password_policy_enforced: {
    meaning: 'Enforces minimum password length when passwords are changed or reset. Optional breached-password blocking.',
    shadowScan: 'This control applies only when a user changes or resets a password. It does not force periodic password changes and does not run during login.',
    action: 'Breached-password blocking checks the chosen password against a breach database when enabled.',
  },
  vuln_intel: {
    meaning: 'Matches installed components against known vulnerability intelligence feeds.',
    shadowScan: 'Compares component slugs and versions to known issues and surfaces indicators.',
    action: 'Review identified components and apply vendor updates.',
  },
};

const GROUPS = [
  {
    id: 'account_access',
    title: 'Identity & Access',
    description: 'Controls that reduce account takeover and privileged access abuse.',
    controlKeys: [
      'mfa_coverage_snapshot',
      'password_policy_enforced',
      'admin_geo_guard',
    ],
    owaspIds: ['A01', 'A07'],
  },
  {
    id: 'updates_integrity',
    title: 'Updates & Integrity Protection',
    description: 'Update posture, known vulnerabilities, and integrity signals.',
    controlKeys: [
      'autoupdate_plugins_detect',
    ],
    owaspIds: ['A06', 'A08'],
  },
  {
    id: 'application_hardening',
    title: 'Application Hardening',
    description: 'Configuration and hardening safeguards.',
    controlKeys: [
      'a05_file_editor_enabled',
      'a05_debug_enabled',
      'a02_missing_security_headers',
      'a02_https_not_enforced',
      'a02_insecure_cookies',
      'a03_db_error_leak',
    ],
    owaspIds: ['A02', 'A03', 'A05'],
  },
  {
    id: 'network_request',
    title: 'Network & Request Protection',
    description: 'Inbound and outbound request controls.',
    controlKeys: [
      'a10_unrestricted_http_requests',
      'a10_open_redirect_proxy_endpoints',
    ],
    owaspIds: ['A10'],
  },
  {
    id: 'visibility_evidence',
    title: 'Visibility & Evidence',
    description: 'Logging, monitoring, and evidence readiness.',
    controlKeys: [
      'visibility.evidence',
    ],
    owaspIds: ['A09'],
  },
];

const getControlCopy = (controlKey: string, groupTitle: string) => {
  const normalizedKey = resolveControlKey(controlKey);
  const copy = CONTROL_COPY[normalizedKey];
  if (copy) return copy;
  return {
    meaning: `${groupTitle} signal from recent events.`,
    shadowScan: 'Monitors and reports changes as they occur.',
    action: 'Review recommended actions and apply updates if needed.',
  };
};

const isEnforcementAvailable = (controlKey: string, enforced?: boolean | null) => {
  if (typeof enforced === 'boolean') return true;
  return controlKey.includes('enforce') || controlKey.includes('block') || controlKey.includes('rate_limit');
};

export default function DomainCoverage() {
  const { domain, managedSite } = useOutletContext<{ domain: { id: string; domain_name: string }; managedSite?: { last_heartbeat_at?: string | null } }>();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showAllActions, setShowAllActions] = useState(false);
  const vulnUiEnabled = import.meta.env.VITE_VULN_ENRICHMENT_ENABLED === 'true';

  const logsQuery = useQuery({
    queryKey: ['domain-coverage-logs', domain.id],
    queryFn: async () => {
      const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('domain_logs')
        .select('category, owasp_id, control_key, status, recommended_action, evidence, last_checked_at, created_at, enforced')
        .eq('domain_id', domain.id)
        .gte('created_at', cutoff)
        .or('owasp_id.not.is.null,category.eq.security_control')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const vulnSummaryQuery = useQuery({
    queryKey: ['domain-vuln-coverage', domain.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domain_vuln_findings')
        .select('summary_json, captured_at, source_meta_json')
        .eq('domain_id', domain.id)
        .order('captured_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      return data ?? null;
    },
    enabled: vulnUiEnabled,
  });

  const coverage = useMemo(() => {
    const logs = logsQuery.data || [];
    const latestByControl = new Map<string, any>();
    const duplicates = new Map<string, number>();
    const unknownKeys = new Set<string>();

    logs.forEach((log) => {
      if (!log.control_key) return;
      const normalizedKey = resolveControlKey(log.control_key);
      if (!isKnownControlKey(normalizedKey)) {
        unknownKeys.add(log.control_key);
        return;
      }
      const current = latestByControl.get(normalizedKey);
      const currentCount = duplicates.get(normalizedKey) ?? 0;
      duplicates.set(normalizedKey, currentCount + 1);
      if (!current || log.created_at > current.created_at) {
        latestByControl.set(normalizedKey, { ...log, control_key: normalizedKey });
      }
    });

    const flatControls = Array.from(latestByControl.values());

    if (import.meta.env.DEV) {
      const duplicateKeys = Array.from(duplicates.entries()).filter(([, count]) => count > 1);
      if (duplicateKeys.length > 0 || unknownKeys.size > 0) {
        console.warn('Control registry drift detected', {
          duplicates: duplicateKeys,
          unknownKeys: Array.from(unknownKeys),
          aliases: CONTROL_ALIASES,
        });
      }
    }

    const vulnSummary = (vulnSummaryQuery.data?.summary_json as any) ?? null;
    const vulnMeta = (vulnSummaryQuery.data?.source_meta_json as any) ?? null;
    const vulnCapturedAt = vulnSummaryQuery.data?.captured_at ?? null;
    const vulnCritical = vulnSummary?.by_severity?.critical ?? 0;
    const vulnHigh = vulnSummary?.by_severity?.high ?? 0;
    const vulnTotal = vulnSummary?.total ?? 0;
    const feedStale = vulnMeta?.freshness?.feed_stale ?? true;
    const inventoryStale = vulnMeta?.freshness?.inventory_stale ?? true;
    const vulnHasData = Boolean(vulnCapturedAt);

    let vulnStatus: string = 'not_implemented';
    if (vulnHasData && (feedStale || inventoryStale)) {
      vulnStatus = 'warn';
    } else if (vulnHasData && (vulnCritical > 0 || vulnHigh > 0)) {
      vulnStatus = 'critical';
    } else if (vulnHasData && vulnTotal === 0) {
      vulnStatus = 'ok';
    } else if (vulnHasData) {
      vulnStatus = 'warn';
    }

    const vulnControl = vulnUiEnabled && vulnHasData
      ? {
          control_key: 'vuln_intel',
          status: vulnStatus,
          evidence: {
            total: vulnTotal,
            critical: vulnCritical,
            high: vulnHigh,
            feed_stale: feedStale,
            inventory_stale: inventoryStale,
          },
          last_checked_at: vulnCapturedAt,
          created_at: vulnCapturedAt,
          enforced: false,
          recommended_action: vulnStatus === 'critical'
            ? 'Update vulnerable components with High/Critical issues.'
            : 'Review installed components and apply updates.',
        }
      : null;

    const groups = GROUPS.map((group) => {
      const controls = Array.from(latestByControl.values()).filter((control) => {
        if (control.owasp_id && group.owaspIds.includes(control.owasp_id)) return true;
        return group.controlKeys.includes(control.control_key);
      });

      if (group.id === 'visibility_evidence' && vulnControl) {
        controls.push(vulnControl);
      }

      let groupBucket: 'covered' | 'managed' | 'needs_host' | 'action' | 'planned' = 'planned';
      let lastChecked = '';
      let enforcementAvailable = 0;
      let enforcementActive = 0;

      controls.forEach((control) => {
        const enforcementAvailableForControl = isEnforcementAvailable(control.control_key, control.enforced);
        const normalized = normalizeSecurityStatus({
          status: control.status,
          enforced: control.enforced,
          hasSignals: true,
          enforcementAvailable: enforcementAvailableForControl,
        });
        if (bucketPriority[normalized.bucket] > bucketPriority[groupBucket]) {
          groupBucket = normalized.bucket;
        }

        const candidate = control.last_checked_at || control.created_at;
        if (candidate && candidate > lastChecked) lastChecked = candidate;

        if (enforcementAvailableForControl) {
          enforcementAvailable += 1;
          if (control.enforced) enforcementActive += 1;
        }
      });

      const monitoringCount = controls.length;

      if (monitoringCount === 0) {
        groupBucket = 'planned';
      }

      return {
        ...group,
        controls,
        groupStatus: bucketToBadge(groupBucket),
        groupBucket,
        groupLabel: bucketToLabel(groupBucket),
        lastChecked,
        monitoringCount,
        enforcementAvailable,
        enforcementActive,
      };
    });

    const augmentedControls = vulnControl ? [...flatControls, vulnControl] : flatControls;

    const lastCheckin = augmentedControls.reduce<string>((latest, control) => {
      const candidate = control.last_checked_at || control.created_at || '';
      if (!candidate) return latest;
      return candidate > latest ? candidate : latest;
    }, '');

    const actionRequiredCount = augmentedControls.reduce((count, control) => {
      const normalized = normalizeSecurityStatus({
        status: control.status,
        enforced: control.enforced,
        hasSignals: true,
        enforcementAvailable: isEnforcementAvailable(control.control_key, control.enforced),
      });
      return normalized.badge === 'action' ? count + 1 : count;
    }, 0);

    const monitoringActive = augmentedControls.length;
    const enforcementAvailableTotal = augmentedControls.filter((control) => isEnforcementAvailable(control.control_key, control.enforced)).length;
    const enforcementActiveTotal = augmentedControls.filter((control) => isEnforcementAvailable(control.control_key, control.enforced) && control.enforced).length;

    return {
      groups,
      flatControls: augmentedControls,
      lastCheckin,
      actionRequiredCount,
      monitoringActive,
      enforcementAvailableTotal,
      enforcementActiveTotal,
    };
  }, [logsQuery.data, vulnSummaryQuery.data, vulnUiEnabled]);

  const overallBucket = useMemo((): 'covered' | 'managed' | 'needs_host' | 'action' | 'planned' => {
    const buckets = coverage.groups.map((group) => group.groupBucket);
    return buckets.reduce((current, bucket) => (
      bucketPriority[bucket] > bucketPriority[current] ? bucket : current
    ), 'planned' as const);
  }, [coverage.groups]);

  const overallBadge = badgeMeta(bucketToBadge(overallBucket), bucketToLabel(overallBucket));
  const canonicalHeartbeat = managedSite?.last_heartbeat_at || coverage.lastCheckin || null;
  const lastCheckinMeta = formatTimestamp(canonicalHeartbeat);
  const lastCheckinAge = canonicalHeartbeat ? Date.now() - new Date(canonicalHeartbeat).getTime() : null;
  const freshness = lastCheckinAge == null
    ? 'offline'
    : lastCheckinAge < 15 * 60 * 1000
      ? 'fresh'
      : lastCheckinAge < 2 * 60 * 60 * 1000
        ? 'stale'
        : 'offline';
  const statusCounts = useMemo(
    () =>
      coverage.flatControls.reduce(
        (counts, control) => {
          const normalized = normalizeSecurityStatus({
            status: control.status,
            enforced: control.enforced,
            hasSignals: true,
            enforcementAvailable: isEnforcementAvailable(control.control_key, control.enforced),
          });
          if (normalized.bucket === 'action') counts.action += 1;
          if (normalized.bucket === 'managed') counts.managed += 1;
          if (normalized.bucket === 'needs_host') counts.needsHost += 1;
          if (normalized.bucket === 'covered') counts.covered += 1;
          return counts;
        },
        { action: 0, managed: 0, needsHost: 0, covered: 0 },
      ),
    [coverage.flatControls],
  );

  const prioritizedActions = useMemo(() => {
    const actions = coverage.flatControls.map((control) => {
      const enforcementAvailable = isEnforcementAvailable(control.control_key, control.enforced);
      const normalized = normalizeSecurityStatus({
        status: control.status,
        enforced: control.enforced,
        hasSignals: true,
        enforcementAvailable,
      });
      if (!['action', 'managed', 'needs_host'].includes(normalized.bucket)) {
        return null;
      }
      const severity = normalized.bucket === 'action'
        ? 'critical'
        : normalized.bucket === 'needs_host'
          ? 'high'
          : 'medium';
      const owner = normalized.bucket === 'managed'
        ? 'ShadowScan'
        : normalized.bucket === 'needs_host'
          ? 'Host/Developer'
          : 'Customer';
      const lastSeenValue = control.last_checked_at || control.created_at || null;
      const lastSeenMs = lastSeenValue ? new Date(lastSeenValue).getTime() : 0;
      const whyNow = normalized.why || control.recommended_action || 'Review this control in ShadowScan.';
      const cta = normalized.bucket === 'managed'
        ? { label: 'View instructions', href: `#control-${control.control_key}` }
        : normalized.bucket === 'needs_host'
          ? { label: 'Request help', href: 'mailto:support@shadowscan.com.au' }
          : { label: 'View details', href: `#control-${control.control_key}` };

      return {
        id: control.control_key,
        title: getControlRegistryEntry(control.control_key)?.title ?? formatControlName(control.control_key),
        severity,
        owner,
        whyNow,
        cta,
        lastSeenMs,
      };
    }).filter(Boolean) as Array<{
      id: string;
      title: string;
      severity: 'critical' | 'high' | 'medium' | 'low';
      owner: string;
      whyNow: string;
      cta: { label: string; href: string };
      lastSeenMs: number;
    }>;

    const severityRank = { critical: 3, high: 2, medium: 1, low: 0 } as const;
    return actions.sort((a, b) => {
      const rankA = severityRank[a.severity] ?? 0;
      const rankB = severityRank[b.severity] ?? 0;
      if (rankA !== rankB) return rankB - rankA;
      if (a.lastSeenMs !== b.lastSeenMs) return b.lastSeenMs - a.lastSeenMs;
      return a.title.localeCompare(b.title);
    });
  }, [coverage.flatControls]);

  const applyFilters = (controls: any[]) => {
    return controls.filter((control) => {
      const normalized = normalizeSecurityStatus({
        status: control.status,
        enforced: control.enforced,
        hasSignals: true,
        enforcementAvailable: isEnforcementAvailable(control.control_key, control.enforced),
      });
      if (statusFilter !== 'all' && normalized.bucket !== statusFilter) return false;
      return true;
    });
  };

  const sortControls = (controls: any[]) => {
    const score = (control: any) => {
      const normalized = normalizeSecurityStatus({
        status: control.status,
        enforced: control.enforced,
        hasSignals: true,
        enforcementAvailable: isEnforcementAvailable(control.control_key, control.enforced),
      });
      const enforcementAvailable = isEnforcementAvailable(control.control_key, control.enforced);
      if (normalized.badge === 'action') return 0;
      if (enforcementAvailable && !control.enforced) return 1;
      if (normalized.badge === 'partial') return 2;
      if (normalized.badge === 'planned') return 3;
      return 4;
    };
    return [...controls].sort((a, b) => score(a) - score(b));
  };

  const actionCountForControls = (controls: any[]) =>
    controls.reduce((count, control) => {
      const normalized = normalizeSecurityStatus({
        status: control.status,
        enforced: control.enforced,
        hasSignals: true,
        enforcementAvailable: isEnforcementAvailable(control.control_key, control.enforced),
      });
      return normalized.bucket === 'action' ? count + 1 : count;
    }, 0);

  const filteredGroups = useMemo(() => {
    if (categoryFilter === 'all') return coverage.groups;
    return coverage.groups.filter((group) => group.id === categoryFilter);
  }, [coverage.groups, categoryFilter]);

  const totalControlCount = coverage.flatControls.length;
  const allActionCount = statusCounts.action;

  return (
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle>{NAV_LABELS.protectionCoverage}</CardTitle>
        <CardDescription>{FRAMEWORK_DISCLOSURE_COPY}</CardDescription>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>Security health</span>
            <Badge variant={overallBadge.variant}>{overallBadge.label}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span>Monitoring active</span>
            <Badge variant="outline">{coverage.monitoringActive}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span>Enforcement active</span>
            <Badge variant="outline">{coverage.enforcementActiveTotal}</Badge>
            <span>Policy tier: —</span>
          </div>
          <div className="flex items-center gap-2">
            <span title={lastCheckinMeta.title}>Last check-in: {lastCheckinMeta.label}</span>
            <Badge variant="outline">
              {freshness === 'fresh' ? 'Fresh' : freshness === 'stale' ? 'Stale' : 'Offline'} · {formatAge(lastCheckinAge ?? null)}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-border/60 bg-muted/10 p-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="danger">{`${STATUS_LABELS.actionRequired} (${statusCounts.action})`}</Badge>
            <Badge variant="warning">{`${STATUS_LABELS.managed} (${statusCounts.managed})`}</Badge>
            <Badge variant="warning">{`${STATUS_LABELS.needsHost} (${statusCounts.needsHost})`}</Badge>
            <Badge variant="success">{`${STATUS_LABELS.covered} (${statusCounts.covered})`}</Badge>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="text-xs text-muted-foreground underline">
                  What do these mean?
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-fit max-w-sm px-3 py-2 text-xs">
                <div className="space-y-2 text-muted-foreground">
                  <div><span className="font-medium text-foreground">{STATUS_LABELS.managed}:</span> {STATUS_DESCRIPTIONS.managed}</div>
                  <div><span className="font-medium text-foreground">{STATUS_LABELS.needsHost}:</span> {STATUS_DESCRIPTIONS.needsHost}</div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="rounded-md border border-border/60 bg-muted/10 p-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={`rounded-md border px-3 py-2 text-xs ${categoryFilter === 'all' ? 'border-primary text-foreground' : 'border-border text-muted-foreground'}`}
              onClick={() => setCategoryFilter('all')}
            >
              <span className="font-medium">All ({totalControlCount})</span>
              {allActionCount > 0 && <span className="ml-2 rounded-full bg-red-500/15 px-2 py-0.5 text-red-600">{allActionCount}</span>}
            </button>
            {coverage.groups.map((group) => {
              const groupActionCount = actionCountForControls(group.controls);
              return (
                <button
                  key={group.id}
                  type="button"
                  className={`rounded-md border px-3 py-2 text-xs ${categoryFilter === group.id ? 'border-primary text-foreground' : 'border-border text-muted-foreground'}`}
                  onClick={() => setCategoryFilter(group.id)}
                >
                  <span className="font-medium">{group.title}</span>
                  {groupActionCount > 0 && (
                    <span className="ml-2 rounded-full bg-red-500/15 px-2 py-0.5 text-red-600">{groupActionCount}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className="rounded-md border border-border/60 bg-muted/10 p-3 text-xs">
          <div className="flex items-center justify-between">
            <div className="font-medium text-sm">Priority Actions</div>
            {prioritizedActions.length > 10 && (
              <button
                type="button"
                className="text-xs text-muted-foreground underline"
                onClick={() => setShowAllActions((prev) => !prev)}
              >
                {showAllActions ? 'Show less' : 'Show all'}
              </button>
            )}
          </div>
          {prioritizedActions.length === 0 ? (
            <div className="text-muted-foreground mt-2">No immediate actions required.</div>
          ) : (
            <div className="mt-2 space-y-2">
              {(showAllActions ? prioritizedActions : prioritizedActions.slice(0, 10)).map((action) => (
                <div key={action.id} className="rounded border border-border/50 bg-background/60 p-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="font-medium">{action.title}</div>
                      <div className="text-muted-foreground">Why now: {action.whyNow}</div>
                      <div className="text-muted-foreground">
                        Severity: {action.severity} · Owner: {action.owner}
                      </div>
                    </div>
                    <a href={action.cta.href} className="text-xs underline text-muted-foreground">
                      {action.cta.label}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <button
            type="button"
            className={`rounded-full border px-3 py-1 ${statusFilter === 'all' ? 'border-foreground text-foreground' : 'border-border text-muted-foreground'}`}
            onClick={() => setStatusFilter('all')}
          >
            All ({totalControlCount})
          </button>
          <button
            type="button"
            className={`rounded-full border px-3 py-1 ${statusFilter === 'action' ? 'border-red-500 text-red-600' : 'border-border text-muted-foreground'}`}
            onClick={() => setStatusFilter('action')}
          >
            {STATUS_LABELS.actionRequired} ({statusCounts.action})
          </button>
          <button
            type="button"
            className={`rounded-full border px-3 py-1 ${statusFilter === 'managed' ? 'border-amber-500 text-amber-600' : 'border-border text-muted-foreground'}`}
            onClick={() => setStatusFilter('managed')}
          >
            {STATUS_LABELS.managed} ({statusCounts.managed})
          </button>
          <button
            type="button"
            className={`rounded-full border px-3 py-1 ${statusFilter === 'needs_host' ? 'border-slate-500 text-slate-700' : 'border-border text-muted-foreground'}`}
            onClick={() => setStatusFilter('needs_host')}
          >
            {STATUS_LABELS.needsHost} ({statusCounts.needsHost})
          </button>
          <button
            type="button"
            className={`rounded-full border px-3 py-1 ${statusFilter === 'covered' ? 'border-emerald-500 text-emerald-600' : 'border-border text-muted-foreground'}`}
            onClick={() => setStatusFilter('covered')}
          >
            {STATUS_LABELS.covered} ({statusCounts.covered})
          </button>
        </div>
        <Accordion type="multiple" className="space-y-2">
          {filteredGroups.map((group) => {
            const badge = badgeMeta(group.groupStatus as 'covered' | 'partial' | 'action' | 'planned', group.groupLabel);
            const controls = sortControls(applyFilters(group.controls));
            return (
              <AccordionItem key={group.id} value={group.id} className="border border-border/60 rounded-md px-3">
                <AccordionTrigger className="flex items-center justify-between text-sm">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{group.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {group.description}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      <span title={formatTimestamp(group.lastChecked).title}>
                        Last checked {formatTimestamp(group.lastChecked).label}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    <span className="text-xs text-muted-foreground">
                      Monitoring: {group.monitoringCount} · Enforcement available: {group.enforcementAvailable} · Active: {group.enforcementActive}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  {controls.length === 0 ? (
                    <div className="text-xs text-muted-foreground">{STATUS_LABELS.monitoringPlanned}.</div>
                  ) : (
                    <div className="space-y-2">
                      {controls.map((control) => {
                        const enforcementAvailable = isEnforcementAvailable(control.control_key, control.enforced);
                        const normalized = normalizeSecurityStatus({
                          status: control.status,
                          enforced: control.enforced,
                          hasSignals: true,
                          enforcementAvailable,
                        });
                        const controlBadge = badgeMeta(normalized.badge, normalized.text);
                        const copy = getControlCopy(control.control_key, group.title);
                        const registryEntry = getControlRegistryEntry(control.control_key);
                        const controlTitle = registryEntry?.title ?? formatControlName(control.control_key);
                        const evidence = control.evidence ?? {};
                        const lastSeenValue = control.last_checked_at || control.created_at || null;
                        const lastSeenMeta = formatTimestamp(lastSeenValue);
                        const lastSeenMs = lastSeenValue ? new Date(lastSeenValue).getTime() : null;
                        const hasTelemetry = !!lastSeenMs && !Number.isNaN(lastSeenMs) && lastSeenMs > 946684800000;
                        const isStale = hasTelemetry && (Date.now() - lastSeenMs) > 2 * 60 * 60 * 1000;
                        const reasonBucket = (() => {
                          if (!hasTelemetry) return 'No recent telemetry yet';
                          if (isStale) return 'Telemetry stale';
                          if (normalized.badge === 'action' && enforcementAvailable && !control.enforced) return 'Needs approval';
                          if (control.control_key.includes('abuse') || control.control_key.includes('failed_login') || control.control_key.includes('rate_limit')) {
                            return 'Detected abuse';
                          }
                          return 'Misconfiguration';
                        })();
                        const nextAction = control.recommended_action || copy.action;
                        const passwordPolicyMeta = control.control_key === 'password_policy_enforced'
                          ? {
                              enforcedOnChange: evidence.enforced_on_change ?? true,
                              minimumLength: typeof evidence.min_length === 'number' ? evidence.min_length : 12,
                              minimumLengthEnabled: Boolean(evidence.enforce_min_length ?? evidence.minimum_length_enforced),
                              breachBlocking: evidence.breached_password_check
                                ? String(evidence.breached_password_check)
                                : (evidence.enforce_breached ? 'enabled' : 'disabled'),
                              lastChangeAt: evidence.last_password_change_at,
                              failedAttempts: evidence.failed_policy_attempts,
                            }
                          : null;
                        return (
                          <div id={`control-${control.control_key}`} key={control.control_key} className="rounded-md border border-border/50 bg-muted/10 p-3">
                            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                              <div className="flex flex-col gap-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-medium">{controlTitle}</span>
                                  <Badge variant={controlBadge.variant}>{controlBadge.label}</Badge>
                                  {normalized.why && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button type="button" className="text-xs text-muted-foreground underline">
                                          Why
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-fit max-w-xs px-3 py-2 text-xs">
                                        <div className="font-medium">Why</div>
                                        <div className="text-muted-foreground">{normalized.why}</div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                  {normalized.badge === 'action' && (
                                    <Badge variant="outline">{reasonBucket}</Badge>
                                  )}
                                  {typeof control.enforced === 'boolean' && (
                                    <Badge variant={control.enforced ? 'success' : 'neutral'}>
                                      {control.enforced ? 'Enforced' : 'Not enforced'}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground">Why: {copy.meaning}</div>
                                <div className="text-xs text-muted-foreground">Next action: {nextAction}</div>
                              </div>
                              <div className="text-xs text-muted-foreground">
                      <span title={lastSeenMeta.title}>Last seen: {lastSeenMeta.label}</span>
                              </div>
                            </div>
                            <details className="mt-2 text-xs text-muted-foreground">
                              <summary className="cursor-pointer">View details</summary>
                              <div className="mt-2 space-y-1">
                                <div>What ShadowScan does: {copy.shadowScan}</div>
                                <div>What you can do: {copy.action}</div>
                                {passwordPolicyMeta && (
                                  <>
                                    <div>Enforced on change/reset: {passwordPolicyMeta.enforcedOnChange ? 'true' : 'false'}</div>
                                    <div>
                                      Minimum length (admins): {passwordPolicyMeta.minimumLengthEnabled ? passwordPolicyMeta.minimumLength : 'Disabled'}
                                    </div>
                                    <div>Breached-password blocking: {passwordPolicyMeta.breachBlocking}</div>
                                    <div>
                                      Last password change: {passwordPolicyMeta.lastChangeAt ? formatTimestampSafe(passwordPolicyMeta.lastChangeAt * 1000) : '—'}
                                    </div>
                                    {typeof passwordPolicyMeta.failedAttempts === 'number' && (
                                      <div>Recent blocked attempts: {passwordPolicyMeta.failedAttempts}</div>
                                    )}
                                  </>
                                )}
                                {control.recommended_action && (
                                  <div>Recommended: {control.recommended_action}</div>
                                )}
                              </div>
                            </details>
                          </div>
                        );
                      })}
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer">{OWASP_MAPPING_LABELS.technicalHeading}</summary>
                        <div className="mt-2 space-y-1">
                          <div>{OWASP_MAPPING_LABELS.internalOnly}</div>
                          <div>Mapped categories: {group.owaspIds.join(', ') || '—'}</div>
                          <div>Control keys: {group.controls.map((control) => control.control_key).join(', ') || '—'}</div>
                        </div>
                      </details>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
}
